/**
 * Single source of proportion for the catalog graph.
 *
 * Node radius, edge width, label size, spacing and link distance are all
 * expressed as ratios of NODE_BASE_RADIUS, so retuning one dimension keeps the
 * rest in step instead of drifting out of proportion.
 *
 * The three multipliers threaded through these helpers (`nodeScale`,
 * `gapScale`, `edgeScale`) are what the display sliders drive. They are kept
 * independent on purpose: zoom-to-fit normalises the whole layout, so growing
 * every dimension together changes nothing on screen — only the *ratio* of node
 * size to gap to edge width is visible.
 */

/** Radius of a System node in graph units. Everything else is a ratio of this. */
export const NODE_BASE_RADIUS = 9;

const NODE_RADIUS_RATIOS: Record<string, number> = {
  system: 1,
  component: 0.55,
  resource: 0.48,
  api: 0.48,
  customer: 0.65,
};

export function nodeRadius(kind: string, nodeScale = 1): number {
  return NODE_BASE_RADIUS * (NODE_RADIUS_RATIOS[kind] ?? 0.48) * nodeScale;
}

/** Dependency edge width per criticality tier. */
export const EDGE_WIDTH_RATIOS: Record<string, number> = {
  critical: 0.26,
  high: 0.2,
  medium: 0.16,
  low: 0.13,
  unknown: 0.13,
};

/** Parent→child edges are structural, so they sit under the thinnest dependency. */
export const HAS_PART_WIDTH_RATIO = 0.1;

/** System→customer edges are informational, slightly bolder than structural ones. */
export const CUSTOMER_LINK_WIDTH_RATIO = 0.12;

export function edgeWidth(ratio: number, edgeScale = 1): number {
  return NODE_BASE_RADIUS * ratio * edgeScale;
}

/** Dash pattern for non-critical edges, as ratios so dashes scale with width. */
export const EDGE_DASH_RATIOS: [number, number] = [0.55, 0.34];

export const LABEL_FONT_RATIO = 1;
/** Gap between the top of a node and the baseline of its label. */
export const LABEL_GAP_RATIO = 0.55;
export const LABEL_HALO_RATIO = 0.26;
/** Clear space kept around a node by the collision force. */
export const COLLISION_PAD_RATIO = 0.85;
export const ARROW_RATIO = 0.62;
/** Distance an edge stops short of its target so the arrowhead stays visible. */
export const ARROW_CLEARANCE_RATIO = 0.36;

export function collisionRadius(kind: string, nodeScale = 1, gapScale = 1): number {
  return nodeRadius(kind, nodeScale) + NODE_BASE_RADIUS * COLLISION_PAD_RATIO * gapScale;
}

const LINK_DISTANCE_RATIOS: Record<string, number> = {
  dependsOn: 5,
  hasPart: 2.4,
  servesCustomer: 3.5,
};

export function linkDistance(relation: string, nodeScale = 1, gapScale = 1): number {
  const ratio = LINK_DISTANCE_RATIOS[relation] ?? LINK_DISTANCE_RATIOS.hasPart;
  // Anchored on the node so connected nodes never end up closer than they are wide.
  return NODE_BASE_RADIUS * ratio * Math.max(nodeScale, 1) * gapScale;
}

/** Cut-off for the repulsion force; beyond this nodes stop pushing each other. */
export function chargeDistanceMax(gapScale = 1): number {
  return NODE_BASE_RADIUS * 24 * gapScale;
}

// ── Zoom compensation ────────────────────────────────────────────────────────
//
// A size of `units` graph units is drawn at `units * k` CSS pixels under zoom
// scale `k`. Left alone, a hairline edge vanishes when zoomed out and a label
// grows to swamp the nodes when zoomed in, which is what breaks the ratio.

/** Below this apparent width an edge is effectively invisible. */
export const EDGE_SCREEN_MIN_PX = 1;
/** Above this an edge reads as a shape rather than a line. */
export const EDGE_SCREEN_MAX_PX = 6;
export const LABEL_SCREEN_MIN_PX = 8;
export const LABEL_SCREEN_MAX_PX = 15;
/** Zoomed out further than this, labels are an unreadable smear, so hide them. */
export const LABEL_HIDE_BELOW_K = 0.35;

/**
 * Adjusts a graph-unit size so its on-screen size stays within [minPx, maxPx].
 * Returns `units` unchanged while the apparent size is already in range, so
 * elements keep their authored proportion over the usual zoom band.
 */
export function clampToScreen(
  units: number,
  k: number,
  minPx: number,
  maxPx = Infinity,
): number {
  const scale = Math.max(k, 1e-6);
  const apparent = units * scale;
  if (apparent < minPx) return minPx / scale;
  if (apparent > maxPx) return maxPx / scale;
  return units;
}
